// src/pages/AdminUsersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";
import { Users, Search } from "lucide-react";

import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

const PAGE_SIZE = 10;

const AdminUsersPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [users, setUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // state untuk stats
  const [totalUsersCount, setTotalUsersCount] = useState(0);

  // state untuk editing PROFIL (Username, Role, Phone)
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    username: "",
    phone: "",
    role: "user",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [saveProfileMessage, setSaveProfileMessage] = useState("");

  // state untuk editing SALDO (Credit, Wallet, Note)
  const [editingSaldo, setEditingSaldo] = useState(null);
  const [saldoForm, setSaldoForm] = useState({
    credit_balance: 0,
    wallet_balance: 0,
    transaction_type: 'bonus', // 'bonus' | 'purchase'
    manual_money_paid: 0,
    note: "",
  });
  const [savingSaldo, setSavingSaldo] = useState(false);
  const [saveSaldoMessage, setSaveSaldoMessage] = useState("");

  // state untuk modal layanan (user_features)
  const [serviceUser, setServiceUser] = useState(null);
  const [allFeatures, setAllFeatures] = useState([]); // dari tabel app_features
  const [serviceCodes, setServiceCodes] = useState([]); // fitur yg sedang dicentang di UI (slugs)
  const [initialServiceCodes, setInitialServiceCodes] = useState([]); // snapshot awal dari DB
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesSaving, setServicesSaving] = useState(false);
  const [servicesMessage, setServicesMessage] = useState("");

  // state untuk BULK ASSIGNMENT (Feature-first flow)
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('assign'); // 'assign' or 'remove'
  const [bulkStep, setBulkStep] = useState(1); // 1 = select features, 2 = select users
  const [bulkSelectedFeatures, setBulkSelectedFeatures] = useState([]);
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);
  const [bulkUserSearch, setBulkUserSearch] = useState(''); // search query for filtering users
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");

  const displayName =
    profile?.username || user?.email?.split("@")[0] || "Admin";

  const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatNumber = (value) =>
    new Intl.NumberFormat("id-ID").format(value || 0);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("id-ID", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  // ---------- cek user admin ----------
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoadingUser(true);

        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate("/login");
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") {
          throw profileError;
        }

        // contoh validasi tambahan: wajib punya phone
        if (!profileData || !profileData.phone) {
          navigate("/login");
          return;
        }

        // hanya admin yang boleh masuk halaman ini
        if (profileData.role !== "admin") {
          navigate("/dashboard");
          return;
        }

        if (!isMounted) return;

        setUser(authUser);
        setProfile(profileData);

        // Fetch Total Users Count
        const { count, error: countErr } = await supabase
          .from("profiles")
          .select("*", { count: "exact", head: true });

        if (!countErr) {
          setTotalUsersCount(count || 0);
        }

      } catch (err) {
        console.error("Error fetching admin data:", err);
      } finally {
        if (isMounted) setLoadingUser(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ---------- load users: AMBIL SEMUA ROLE ----------
  const loadUsers = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const { data, error: profilesErr } = await supabase
        .from("profiles")
        .select(
          "id, username, email, role, credit_balance, wallet_balance, phone, created_at"
        )
        .order("created_at", { ascending: false })
        .limit(500);

      if (profilesErr) throw profilesErr;

      setUsers(data || []);

      // Update count locally just in case
      if (data) setTotalUsersCount(data.length);

    } catch (err) {
      console.error("Error loading users:", err);
      setError(err.message || "Gagal memuat data pengguna.");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!loadingUser && user) {
      loadUsers();
    }
  }, [loadingUser, user]);

  // ---------- filter & search ----------
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const q = searchTerm.toLowerCase();

    return users.filter((u) => {
      const username = (u.username || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const phone = (u.phone || "").toLowerCase();
      const role = (u.role || "").toLowerCase();
      return (
        username.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        role.includes(q)
      );
    });
  }, [users, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ---------- pagination ----------
  const totalPages = pageSize === 'all'
    ? 1
    : Math.max(1, Math.ceil(filteredUsers.length / pageSize) || 1);

  const paginatedUsers = useMemo(() => {
    if (pageSize === 'all') return filteredUsers;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, pageSize]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  // ---------- handle edit PROFIL ----------
  const openEditProfile = (u) => {
    setEditingProfile(u);
    setProfileForm({
      username: u.username || "",
      phone: u.phone || "",
      role: u.role || "user",
    });
    setSaveProfileMessage("");
  };

  const handleProfileFormChange = (field, value) => {
    setProfileForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProfile = async () => {
    if (!editingProfile) return;

    if (user && editingProfile.id === user.id && profileForm.role !== "admin") {
      setSaveProfileMessage(
        "Tidak boleh menurunkan role akun admin yang sedang login."
      );
      return;
    }

    try {
      setSavingProfile(true);
      setSaveProfileMessage("");

      const payload = {
        username: profileForm.username || null,
        phone: profileForm.phone || null,
        role: profileForm.role || "user",
      };

      const { error: updateErr } = await supabase
        .from("profiles")
        .update(payload)
        .eq("id", editingProfile.id);

      if (updateErr) {
        console.error("Error updating user:", updateErr);
        setSaveProfileMessage(
          updateErr.message || "Gagal menyimpan perubahan."
        );
        return;
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingProfile.id
            ? { ...u, ...payload }
            : u
        )
      );

      setSaveProfileMessage("Profil berhasil disimpan.");
    } catch (err) {
      console.error("Error updating user:", err);
      setSaveProfileMessage(err.message || "Gagal menyimpan perubahan.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelEditProfile = () => {
    setEditingProfile(null);
    setSaveProfileMessage("");
    setSavingProfile(false);
  };

  // ---------- handle edit SALDO ----------
  const openEditSaldo = (u) => {
    setEditingSaldo(u);
    setSaldoForm({
      credit_balance: u.credit_balance ?? 0,
      wallet_balance: u.wallet_balance ?? 0,
      transaction_type: 'bonus',
      manual_money_paid: 0,
      note: "",
    });
    setSaveSaldoMessage("");
  };

  const handleSaldoFormChange = (field, value) => {
    setSaldoForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSaldo = async () => {
    if (!editingSaldo) return;

    try {
      setSavingSaldo(true);
      setSaveSaldoMessage("");

      const newCredit = Math.max(0, Number(saldoForm.credit_balance) || 0);
      const newWallet = Math.max(0, Number(saldoForm.wallet_balance) || 0);

      const oldCredit = editingSaldo.credit_balance || 0;
      const oldWallet = editingSaldo.wallet_balance || 0;

      const creditDiff = newCredit - oldCredit;
      const walletDiff = newWallet - oldWallet;

      // Only proceed if there is a change or a note
      if (creditDiff === 0 && walletDiff === 0 && !saldoForm.note.trim()) {
        setSaveSaldoMessage("Tidak ada perubahan.");
        setSavingSaldo(false);
        return;
      }

      // 4. Validation Rules (NEW)
      if (creditDiff !== 0) {
        // If Credit is changed...
        if (saldoForm.transaction_type === 'purchase') {
          // Rule 1: Must have Money > 0
          if (!saldoForm.manual_money_paid || Number(String(saldoForm.manual_money_paid || 0).replace(/[^\d]/g, '')) <= 0) {
            setSaveSaldoMessage("Gagal: Untuk Pembelian Manual, Nominal Uang wajib belum diisi");
            setSavingSaldo(false);
            return;
          }
          // Rule 2: Must have Note
          if (!saldoForm.note || !saldoForm.note.trim()) {
            setSaveSaldoMessage("Gagal: Untuk Pembelian Manual, Catatan wajib belum diisi");
            setSavingSaldo(false);
            return;
          }
        }
      }

      // Sanitize money
      const moneyPaidSafe = saldoForm.manual_money_paid
        ? Number(String(saldoForm.manual_money_paid).replace(/[^\d]/g, ''))
        : 0;

      // --- USE EDGE FUNCTION FOR ATOMIC TRANSACTION & RLS BYPASS ---
      console.log("INVOKING EDGE FUNCTION: admin-manual-transaction");

      const { data: funcData, error: funcError } = await supabase.functions.invoke('admin-manual-transaction', {
        body: {
          target_user_id: editingSaldo.id,
          credit_add: creditDiff,
          wallet_add: walletDiff,
          money_paid: moneyPaidSafe,
          note: saldoForm.note || "",
          type: saldoForm.transaction_type
        }
      });

      if (funcError) {
        console.error("EDGE FUNCTION ERROR:", funcError);
        throw new Error(funcError.message || "Gagal memproses transaksi di server.");
      }

      console.log("EDGE FUNCTION RESULT:", funcData);

      // Update Local State on Success
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingSaldo.id
            ? {
              ...u,
              credit_balance: newCredit,
              wallet_balance: newWallet
            }
            : u
        )
      );

      setSaveSaldoMessage("Saldo berhasil disimpan (Transaksi Tercatat).");

    } catch (err) {
      console.error("Error updating saldo:", err);
      // Detailed error message handling
      let msg = err.message || "Gagal menyimpan saldo.";
      if (msg.includes("functions_invoke_error")) msg = "Gagal menghubungi server.";
      setSaveSaldoMessage(msg);
    } finally {
      setSavingSaldo(false);
    }
  };

  const handleCancelEditSaldo = () => {
    setEditingSaldo(null);
    setSaveSaldoMessage("");
    setSavingSaldo(false);
  };

  // ---------- modal layanan: load fitur + user_features ----------
  const openServicesModal = async (u) => {
    setServiceUser(u);
    setServicesMessage("");
    setServicesLoading(true);
    setAllFeatures([]);
    setServiceCodes([]);
    setInitialServiceCodes([]);

    try {
      const [
        { data: featuresData, error: featuresErr },
        { data: userFeaturesData, error: ufErr },
      ] = await Promise.all([
        supabase
          .from("app_features") // Changed from 'features' to 'app_features'
          .select("slug, name, description") // Updated to select slug
          .eq("is_active", true)
          .order("sort_order", { ascending: true }),
        supabase
          .from("user_features")
          .select("feature_code")
          .eq("user_id", u.id),
      ]);

      if (featuresErr) throw featuresErr;
      if (ufErr) throw ufErr;

      // feature_code in user_features now maps to app_features.slug
      const codes = (userFeaturesData || []).map((r) => r.feature_code);

      setAllFeatures(featuresData || []);
      setServiceCodes(codes);
      setInitialServiceCodes(codes);
    } catch (err) {
      console.error("Error loading user services:", err);
      setServicesMessage(err.message || "Gagal memuat layanan pengguna ini.");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleToggleServiceCode = (slug) => {
    setServiceCodes((prev) =>
      prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug]
    );
  };

  const handleSaveServices = async () => {
    if (!serviceUser) return;
    setServicesSaving(true);
    setServicesMessage("");

    try {
      const toAdd = serviceCodes.filter((c) => !initialServiceCodes.includes(c));
      const toRemove = initialServiceCodes.filter(
        (c) => !serviceCodes.includes(c)
      );

      // 1. Remove unchecked
      if (toRemove.length > 0) {
        const { error: rmErr } = await supabase
          .from("user_features")
          .delete()
          .eq("user_id", serviceUser.id)
          .in("feature_code", toRemove);
        if (rmErr) throw rmErr;
      }

      // 2. Add new checked
      if (toAdd.length > 0) {
        const toInsert = toAdd.map((slug) => ({
          user_id: serviceUser.id,
          feature_code: slug,
        }));
        const { error: addErr } = await supabase
          .from("user_features")
          .insert(toInsert);
        if (addErr) throw addErr;
      }

      setServicesMessage("Layanan berhasil diperbarui.");
      setInitialServiceCodes([...serviceCodes]);
    } catch (err) {
      console.error("Error saving services:", err);
      setServicesMessage("Gagal menyimpan: " + err.message);
    } finally {
      setServicesSaving(false);
    }
  };

  const handleCloseServicesModal = () => {
    setServiceUser(null);
    setServicesMessage("");
    setServiceCodes([]);
  };

  // ---------- BULK ASSIGNMENT HANDLERS (Feature-first flow) ----------
  const openBulkModal = async (mode = 'assign') => {
    setBulkModalOpen(true);
    setBulkMode(mode);
    setBulkStep(1);
    setBulkSelectedFeatures([]);
    setBulkSelectedUsers([]);
    setBulkUserSearch('');
    setBulkMessage("");

    // Load features if not loaded
    if (allFeatures.length === 0) {
      try {
        const { data, error } = await supabase
          .from("app_features")
          .select("slug, name, description")
          .eq("is_active", true)
          .order("sort_order", { ascending: true });
        if (error) throw error;
        setAllFeatures(data || []);
      } catch (err) {
        console.error("Error loading features:", err);
      }
    }
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkStep(1);
    setBulkSelectedFeatures([]);
    setBulkSelectedUsers([]);
    setBulkMessage("");
  };

  const handleToggleBulkFeature = (slug) => {
    setBulkSelectedFeatures(prev =>
      prev.includes(slug)
        ? prev.filter(s => s !== slug)
        : [...prev, slug]
    );
  };

  const handleToggleBulkUser = (userId) => {
    setBulkSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllBulkUsers = () => {
    if (bulkSelectedUsers.length === filteredUsers.length) {
      setBulkSelectedUsers([]);
    } else {
      setBulkSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const goToUserSelection = () => {
    if (bulkSelectedFeatures.length === 0) {
      setBulkMessage("Pilih minimal 1 fitur terlebih dahulu.");
      return;
    }
    setBulkStep(2);
    setBulkMessage("");
  };

  const backToFeatureSelection = () => {
    setBulkStep(1);
    setBulkMessage("");
  };

  const handleBulkAssign = async (assignToAll = false) => {
    if (bulkSelectedFeatures.length === 0) {
      setBulkMessage("Pilih minimal 1 fitur.");
      return;
    }

    const targetUserIds = assignToAll ? users.map(u => u.id) : bulkSelectedUsers;

    if (!assignToAll && targetUserIds.length === 0) {
      setBulkMessage("Pilih minimal 1 user atau klik 'Semua User'.");
      return;
    }

    if (assignToAll) {
      const action = bulkMode === 'assign' ? 'aktifkan' : 'hapus';
      const confirm = window.confirm(
        `Yakin ${action} ${bulkSelectedFeatures.length} fitur untuk SEMUA ${users.length} user?`
      );
      if (!confirm) return;
    }

    try {
      setBulkAssigning(true);
      setBulkMessage("");

      if (bulkMode === 'assign') {
        // ASSIGN MODE: Insert features
        const toInsert = [];
        for (const userId of targetUserIds) {
          for (const featureSlug of bulkSelectedFeatures) {
            toInsert.push({ user_id: userId, feature_code: featureSlug });
          }
        }

        const { error } = await supabase
          .from("user_features")
          .upsert(toInsert, { onConflict: 'user_id,feature_code', ignoreDuplicates: true });

        if (error) throw error;
      } else {
        // REMOVE MODE: Delete features
        const deletePromises = [];
        for (const userId of targetUserIds) {
          deletePromises.push(
            supabase
              .from("user_features")
              .delete()
              .eq("user_id", userId)
              .in("feature_code", bulkSelectedFeatures)
          );
        }

        const results = await Promise.all(deletePromises);
        const errors = results.filter(r => r.error);
        if (errors.length > 0) throw errors[0].error;
      }

      const userCount = assignToAll ? users.length : targetUserIds.length;
      const action = bulkMode === 'assign' ? 'ditambahkan ke' : 'dihapus dari';
      setBulkMessage(
        `✅ Berhasil! ${bulkSelectedFeatures.length} fitur ${action} ${userCount} user.`
      );

      setTimeout(() => {
        closeBulkModal();
      }, 2000);

    } catch (err) {
      console.error("Error bulk operation:", err);
      setBulkMessage("❌ Gagal: " + err.message);
    } finally {
      setBulkAssigning(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500">
        Memuat Admin...
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      <DashboardSidebarAdmin />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <DashboardNavbarAdmin />

        <main className="flex-1 p-6 space-y-6 overflow-y-auto">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Manajemen Pengguna</h1>
              <p className="text-sm text-slate-500">
                Kelola data pengguna, Saldo, dan akses layanan.
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Total Users Card - Clean & Simple */}
              <div className="hidden sm:flex items-center gap-3 px-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <Users size={18} strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Total Users</div>
                  <div className="text-xl font-bold text-slate-800 leading-none">{totalUsersCount}</div>
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Cari user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 rounded-xl border-slate-200 pl-10 pr-4 py-2 text-sm focus:border-sky-500 focus:ring-sky-500 shadow-sm"
                />
                <svg
                  className="absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Page Size Selector & Bulk Button */}
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium">Baris per halaman:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const value = e.target.value === 'all' ? 'all' : Number(e.target.value);
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent cursor-pointer transition-all"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">Semua ({filteredUsers.length})</option>
              </select>
            </div>

            {/* Bulk Feature Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => openBulkModal('assign')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-semibold rounded-lg shadow-md transition-all hover:shadow-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Aktifkan Fitur Bulk
              </button>
              <button
                onClick={() => openBulkModal('remove')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white text-xs font-semibold rounded-lg shadow-md transition-all hover:shadow-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
                Hapus Fitur Bulk
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              {loadingData ? (
                <div className="p-8 text-center text-slate-500">Memuat data pengguna...</div>
              ) : error ? (
                <div className="p-8 text-center text-rose-500">{error}</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Tidak ada pengguna ditemukan.
                </div>
              ) : (
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-6 py-4 font-semibold w-16">No</th>
                      <th className="px-6 py-4 font-semibold">User</th>
                      <th className="px-6 py-4 font-semibold">Role</th>
                      <th className="px-6 py-4 font-semibold">Kredit API</th>
                      <th className="px-6 py-4 font-semibold">Wallet</th>
                      <th className="px-6 py-4 font-semibold">Phone</th>
                      <th className="px-6 py-4 font-semibold">Joined</th>
                      <th className="px-6 py-4 font-semibold text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedUsers.map((u, index) => (
                      <tr key={u.id} className="hover:bg-slate-50/50">
                        <td className="px-6 py-4 text-slate-500 font-medium">
                          {pageSize === 'all' ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-800">
                            {u.username || "No Username"}
                          </div>
                          <div className="text-xs text-slate-400">{u.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${u.role === "admin"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-slate-100 text-slate-700"
                              }`}
                          >
                            {u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-emerald-600">
                          {formatNumber(u.credit_balance)}
                        </td>
                        <td className="px-6 py-4 font-medium text-blue-600">
                          {formatRupiah(u.wallet_balance)}
                        </td>
                        <td className="px-6 py-4 text-slate-500">
                          {u.phone || "-"}
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-400">
                          {formatDateTime(u.created_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditProfile(u)}
                              className="rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-100 transition-colors"
                            >
                              Profile
                            </button>
                            <button
                              onClick={() => openEditSaldo(u)}
                              className="rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-100 transition-colors"
                            >
                              Saldo
                            </button>
                            <button
                              onClick={() => openServicesModal(u)}
                              className="rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-600 hover:bg-sky-100 transition-colors"
                            >
                              Layanan
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {!loadingData && pageSize !== 'all' && totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
                <div className="text-sm text-slate-500">
                  Menampilkan {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredUsers.length)} dari {filteredUsers.length} user
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* MODAL EDIT PROFILE */}
      {editingProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/5">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">Profil</h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Username */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Username</label>
                <input
                  type="text"
                  value={profileForm.username}
                  onChange={(e) => handleProfileFormChange("username", e.target.value)}
                  className="w-full rounded-lg border-slate-200 text-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Role</label>
                <select
                  value={profileForm.role}
                  onChange={(e) => handleProfileFormChange("role", e.target.value)}
                  className="w-full rounded-lg border-slate-200 text-sm focus:border-sky-500 focus:ring-sky-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">No HP</label>
                <input
                  type="text"
                  value={profileForm.phone}
                  onChange={(e) => handleProfileFormChange("phone", e.target.value)}
                  className="w-full rounded-lg border-slate-200 text-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>

              {saveProfileMessage && (
                <div
                  className={`text-xs px-2 py-1.5 rounded-lg ${saveProfileMessage.includes("berhasil")
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                    }`}
                >
                  {saveProfileMessage}
                </div>
              )}
            </div>

            <div className="bg-slate-50 px-6 py-4 flex justify-end gap-2 border-t border-slate-100">
              <button
                onClick={handleCancelEditProfile}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-70"
              >
                {savingProfile ? "Menyimpan..." : "Simpan Profil"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* MODAL ATUR SALDO */}
      {editingSaldo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/5">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">Atur Saldo</h3>
              <p className="text-xs text-slate-500">
                User: <span className="font-medium text-slate-700">{editingSaldo.username || editingSaldo.email}</span>
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Credit Balance */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Kredit API (Angka)</label>
                <input
                  type="number"
                  value={saldoForm.credit_balance}
                  onChange={(e) => handleSaldoFormChange("credit_balance", e.target.value)}
                  className="w-full rounded-lg border-slate-200 text-sm focus:border-sky-500 focus:ring-sky-500 font-mono"
                />
              </div>

              {/* Wallet Balance */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Wallet (IDR)</label>
                <input
                  type="number"
                  value={saldoForm.wallet_balance}
                  onChange={(e) => handleSaldoFormChange("wallet_balance", e.target.value)}
                  className="w-full rounded-lg border-slate-200 text-sm focus:border-sky-500 focus:ring-sky-500 font-mono"
                />
              </div>

              {/* Transaction Type Selection */}
              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-medium text-slate-700 mb-2">Tipe Transaksi (Untuk Log)</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="trx_type"
                      checked={saldoForm.transaction_type === 'bonus'}
                      onChange={() => handleSaldoFormChange('transaction_type', 'bonus')}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700">Debug / Bonus (Gratis)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="trx_type"
                      checked={saldoForm.transaction_type === 'purchase'}
                      onChange={() => handleSaldoFormChange('transaction_type', 'purchase')}
                      className="text-sky-600 focus:ring-sky-500"
                    />
                    <span className="text-sm text-slate-700">Pembelian Manual (Bayar)</span>
                  </label>
                </div>
              </div>

              {/* Manual Money Input (Only if Purchase) */}
              {saldoForm.transaction_type === 'purchase' && (
                <div className="animate-in slide-in-from-top-1 fade-in duration-200">
                  <label className="block text-xs font-bold text-sky-700 mb-1">Nominal Uang (IDR)</label>
                  <input
                    type="text"
                    placeholder="Contoh: 50.000"
                    value={saldoForm.manual_money_paid ? formatRupiah(saldoForm.manual_money_paid) : ""}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      handleSaldoFormChange("manual_money_paid", numericValue);
                    }}
                    className="w-full rounded-lg border-sky-300 bg-sky-50 text-sm focus:border-sky-500 focus:ring-sky-500 font-mono text-sky-800 font-bold"
                  />
                  <p className="text-[10px] text-sky-600 mt-1">
                    Masukkan nominal asli yang dibayarkan user (Transfer/Cash).
                  </p>
                </div>
              )}

              {/* Note (Catatan) */}
              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  {saldoForm.transaction_type === 'bonus' ? 'Catatan (Otomatis)' : 'Catatan (Opsional)'}
                  {saldoForm.transaction_type === 'purchase' && <span className="text-slate-400 font-normal"> - Simpan log</span>}
                </label>
                <textarea
                  rows={2}
                  value={saldoForm.transaction_type === 'bonus' ? "Gratisan" : saldoForm.note}
                  onChange={(e) => handleSaldoFormChange("note", e.target.value)}
                  disabled={saldoForm.transaction_type === 'bonus'}
                  placeholder={saldoForm.transaction_type === 'bonus' ? "Gratisan" : "Contoh: Transfer via BCA"}
                  className={`w-full rounded-lg border-slate-200 text-sm focus:border-sky-500 focus:ring-sky-500 ${saldoForm.transaction_type === 'bonus' ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                />
              </div>

              {saveSaldoMessage && (
                <div
                  className={`text-xs px-2 py-1.5 rounded-lg ${saveSaldoMessage.includes("berhasil")
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                    }`}
                >
                  {saveSaldoMessage}
                </div>
              )}
            </div>

            <div className="bg-slate-50 px-6 py-4 flex justify-end gap-2 border-t border-slate-100">
              <button
                onClick={handleCancelEditSaldo}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                onClick={handleSaveSaldo}
                disabled={savingSaldo}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-70"
              >
                {savingSaldo ? "Menyimpan..." : "Simpan Saldo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SERVICES (Manual Activated for User) */}
      {serviceUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/5 flex flex-col max-h-[90vh]">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 shrink-0">
              <h3 className="text-lg font-bold text-slate-800">
                Kelola Layanan
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Pengguna: <span className="font-medium text-slate-700">{serviceUser.username || serviceUser.email}</span>
                <br />
                Centang fitur yang diizinkan untuk pengguna ini.
              </p>
            </div>

            <div className="p-6 overflow-y-auto min-h-0">
              {servicesLoading ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  Memuat daftar fitur...
                </div>
              ) : allFeatures.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  Belum ada fitur di database (app_features).
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {allFeatures.map((f) => {
                    // check if slug is in serviceCodes
                    const checked = serviceCodes.includes(f.slug);
                    return (
                      <label
                        key={f.slug}
                        className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-all ${checked
                          ? "border-sky-200 bg-sky-50/50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                      >
                        <div className="pt-0.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleToggleServiceCode(f.slug)}
                            className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 h-4 w-4"
                          />
                        </div>
                        <div className="flex-1 space-y-0.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-800">
                              {f.name}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                              {f.slug}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">
                            {f.description || "Tidak ada deskripsi."}
                          </p>
                          <p className="text-[10px] pt-1">
                            Status:{" "}
                            <span
                              className={
                                checked
                                  ? "text-emerald-600 font-medium"
                                  : "text-slate-400"
                              }
                            >
                              {checked ? "Aktif" : "Tidak Aktif"}
                            </span>
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {servicesMessage && (
                <div
                  className={`mt-4 text-xs px-3 py-2 rounded-lg ${servicesMessage.toLowerCase().includes("gagal")
                    ? "bg-rose-50 text-rose-700 border border-rose-100"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                    }`}
                >
                  {servicesMessage}
                </div>
              )}
            </div>

            <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={handleCloseServicesModal}
                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Tutup
              </button>
              <button
                type="button"
                onClick={handleSaveServices}
                disabled={servicesSaving || servicesLoading}
                className="px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-70 shadow-sm"
              >
                {servicesSaving ? "Menyimpan..." : "Simpan Layanan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL BULK ASSIGNMENT - 2 STEP FLOW */}
      {bulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10 flex flex-col">
            {/* Header */}
            <div className={`bg-gradient-to-r px-6 py-4 text-white ${bulkMode === 'assign'
              ? 'from-emerald-500 to-green-600'
              : 'from-rose-500 to-red-600'
              }`}>
              <h3 className="text-lg font-bold">
                {bulkMode === 'assign' ? 'Aktifkan Fitur Bulk' : 'Hapus Fitur Bulk'}
              </h3>
              <p className="text-xs mt-1 opacity-90">
                {bulkStep === 1 ? "Langkah 1: Pilih Fitur" : `Langkah 2: Pilih User (${bulkSelectedFeatures.length} fitur dipilih)`}
              </p>
            </div>

            {/* Step Indicator */}
            <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-4">
              <div className={`flex items-center gap-2 ${bulkStep === 1 ? "text-sky-600 font-semibold" : "text-slate-400"
                }`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${bulkStep === 1 ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}>1</div>
                <span className="text-sm">Pilih Fitur</span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200"></div>
              <div className={`flex items-center gap-2 ${bulkStep === 2 ? "text-sky-600 font-semibold" : "text-slate-400"
                }`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${bulkStep === 2 ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}>2</div>
                <span className="text-sm">Pilih User</span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {bulkStep === 1 ? (
                /* STEP 1: PILIH FITUR */
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-slate-600">
                      {bulkMode === 'assign'
                        ? 'Pilih fitur yang ingin diaktifkan untuk user:'
                        : 'Pilih fitur yang ingin dihapus dari user:'}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        if (bulkSelectedFeatures.length === allFeatures.length) {
                          setBulkSelectedFeatures([]);
                        } else {
                          setBulkSelectedFeatures(allFeatures.map(f => f.slug));
                        }
                      }}
                      className="text-xs font-semibold text-sky-600 hover:text-sky-700 px-3 py-1.5 rounded-lg hover:bg-sky-50 transition-colors"
                    >
                      {bulkSelectedFeatures.length === allFeatures.length ? "Batalkan Semua" : "Pilih Semua"}
                    </button>
                  </div>
                  {allFeatures.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">Memuat fitur...</div>
                  ) : (
                    <div className="space-y-2">
                      {allFeatures.map((f) => {
                        const checked = bulkSelectedFeatures.includes(f.slug);
                        return (
                          <label
                            key={f.slug}
                            className={`flex gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${checked
                              ? "border-sky-400 bg-sky-50 shadow-sm"
                              : "border-slate-200 hover:border-sky-200 bg-white hover:bg-slate-50"
                              }`}
                          >
                            <div className="pt-0.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleBulkFeature(f.slug)}
                                className="w-5 h-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-slate-800">{f.name}</span>
                                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono bg-slate-100/70 px-2 py-1 rounded">
                                  {f.slug}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {(f.description && f.description.includes('|||CONFIG|||')
                                  ? f.description.split('|||CONFIG|||')[0].trim()
                                  : f.description) || "Tidak ada deskripsi."}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* STEP 2: PILIH USER */
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-slate-600">
                      Pilih user yang ingin diberi fitur:
                    </p>
                    <button
                      type="button"
                      onClick={handleSelectAllBulkUsers}
                      className="text-xs font-semibold text-sky-600 hover:text-sky-700 px-3 py-1.5 rounded-lg hover:bg-sky-50 transition-colors"
                    >
                      {bulkSelectedUsers.length === filteredUsers.length ? "Batalkan Semua" : "Pilih Semua"}
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Cari user berdasarkan nama atau email..."
                      value={bulkUserSearch}
                      onChange={(e) => setBulkUserSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                    />
                  </div>

                  <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                    {filteredUsers
                      .filter(u => {
                        if (!bulkUserSearch) return true;
                        const searchLower = bulkUserSearch.toLowerCase();
                        return (
                          (u.username && u.username.toLowerCase().includes(searchLower)) ||
                          (u.email && u.email.toLowerCase().includes(searchLower))
                        );
                      })
                      .map((u) => {
                        const checked = bulkSelectedUsers.includes(u.id);
                        return (
                          <label
                            key={u.id}
                            className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-all ${checked
                              ? "border-sky-300 bg-sky-50"
                              : "border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50"
                              }`}
                          >
                            <div className="pt-0.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleBulkUser(u.id)}
                                className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-sm text-slate-800">{u.username || "No Username"}</div>
                              <div className="text-xs text-slate-500">{u.email}</div>
                            </div>
                            <div className={`text-[10px] font-semibold px-2 py-1 rounded-full ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"
                              }`}>
                              {u.role}
                            </div>
                          </label>
                        );
                      })}
                  </div>

                  {bulkSelectedUsers.length > 0 && (
                    <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                      <p className="text-xs text-sky-700 font-semibold">
                        ✓ {bulkSelectedUsers.length} user dipilih
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              {bulkMessage && (
                <div
                  className={`mt-4 text-xs px-3 py-2 rounded-lg font-medium ${bulkMessage.includes("✅")
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}
                >
                  {bulkMessage}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
              {bulkStep === 1 ? (
                /* Step 1 Footer */
                <>
                  <button
                    type="button"
                    onClick={closeBulkModal}
                    className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    onClick={goToUserSelection}
                    disabled={bulkSelectedFeatures.length === 0}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    Lanjut Pilih User
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              ) : (
                /* Step 2 Footer */
                <>
                  <button
                    type="button"
                    onClick={backToFeatureSelection}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Kembali
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkAssign(false)}
                    disabled={bulkAssigning || bulkSelectedUsers.length === 0}
                    className={`px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all ${bulkMode === 'assign'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-rose-600 hover:bg-rose-700'
                      }`}
                  >
                    {bulkAssigning ? "Memproses..." : `${bulkMode === 'assign' ? 'Aktifkan' : 'Hapus'} untuk ${bulkSelectedUsers.length} User`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsersPage;
